import { countConditions } from "@estelle/domain";

import { apiFetch } from "@/adapters/api/base";
import type {
  Id,
  ListQuery,
  Page,
  Transfer,
  TransferEvent,
  Warehouse,
  WarehousesRepository,
  WriteResult,
} from "@estelle/domain";

/**
 * The warehouse master and stock transfers, over HTTP (M12-3, FR-WHS-1..12;
 * #172).
 *
 * Dispatch, receipt and cancel each reach `apps/api`'s write routes, which
 * send an inventory delta through `writeThrough()` (`sync/transfer-writes.ts`).
 * What comes back on success is the transfer re-read from the rows the write
 * just wrote; a refusal is a `WriteResult` rejection to show, not an error to
 * throw.
 */

function listParams(query: ListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor !== undefined) params.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    params.set("search", query.search);
  }
  if (query.filter && countConditions(query.filter) > 0) {
    params.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    params.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return params;
}

async function get<T>(path: string, params?: URLSearchParams): Promise<T> {
  const query = params?.toString() ?? "";
  const response = await apiFetch(
    `/api/warehouses${path}${query ? `?${query}` : ""}`,
  );
  if (!response.ok) {
    throw new Error(
      `Warehouses request failed: ${String(response.status)} on ${path || "/"}`,
    );
  }
  return (await response.json()) as T;
}

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx or
 * 503 status — a rejection to show. Anything else is a transport failure and
 * throws.
 */
async function write(
  path: string,
  body: unknown,
): Promise<WriteResult<Transfer>> {
  const response = await apiFetch(`/api/warehouses${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<Transfer> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Warehouses POST failed: ${String(response.status)} on ${path}`,
  );
}

export function createApiWarehouses(): WarehousesRepository {
  return {
    list: (query: ListQuery): Promise<Page<Warehouse>> =>
      get<Page<Warehouse>>("", listParams(query)),

    get: async (id: Id): Promise<Warehouse | null> => {
      const response = await apiFetch(`/api/warehouses/${id}`);
      if (response.status === 404) return null;
      if (!response.ok) {
        throw new Error(
          `Warehouses request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as Warehouse;
    },

    listTransfers: (query: ListQuery): Promise<Page<Transfer>> =>
      get<Page<Transfer>>("/transfers", listParams(query)),

    getTransfer: async (id: Id): Promise<Transfer | null> => {
      const response = await apiFetch(`/api/warehouses/transfers/${id}`);
      if (response.status === 404) return null;
      if (!response.ok) {
        throw new Error(
          `Warehouses request failed: ${String(response.status)}`,
        );
      }
      return (await response.json()) as Transfer;
    },

    transferHistory: (
      id: Id,
      query?: ListQuery,
    ): Promise<Page<TransferEvent>> =>
      get<Page<TransferEvent>>(
        `/transfers/${encodeURIComponent(id)}/history`,
        listParams(query ?? {}),
      ),

    createTransfer: (input) => write("/transfers", input),

    dispatchTransfer: (id: Id) =>
      write(`/transfers/${encodeURIComponent(id)}/dispatch`, {}),

    receiveTransfer: (id: Id, received) =>
      write(`/transfers/${encodeURIComponent(id)}/receive`, { received }),

    cancelTransfer: (id: Id, reason: string) =>
      write(`/transfers/${encodeURIComponent(id)}/cancel`, { reason }),
  };
}
